import { useEffect, useMemo, useRef, useState } from "react";

interface Dot {
  x: number;
  y: number;
  r: number;
  ox: number;
  oy: number;
  dur: number;
  delay: number;
}

interface ConstellationFieldProps {
  count?: number;
  /** Triggers a brief brand-cyan wash */
  tintKey?: number | null;
  className?: string;
}

/**
 * Slow-orbiting dot field with auto-connecting lines (within ~14% of viewbox).
 * CSS-only orbits, lines recomputed only on resize.
 */
export function ConstellationField({
  count = 50,
  tintKey,
  className,
}: ConstellationFieldProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 800, h: 320 });
  const [tint, setTint] = useState(false);

  useEffect(() => {
    if (!tintKey) return;
    setTint(true);
    const t = setTimeout(() => setTint(false), 1400);
    return () => clearTimeout(t);
  }, [tintKey]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setSize({ w: e.contentRect.width, h: e.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dots: Dot[] = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const a = (i * 9301 + 49297) % 233280;
      const b = (i * 4823 + 2917) % 233280;
      const c = (i * 1499 + 7919) % 233280;
      return {
        x: (a / 233280) * 100,
        y: (b / 233280) * 100,
        r: 0.6 + ((i * 7) % 4) * 0.3,
        ox: 4 + (c / 233280) * 8,
        oy: 4 + ((c * 1.3) % 233280) / 233280 * 8,
        dur: 14 + ((i * 13) % 26),
        delay: -(((i * 5) % 18)),
      };
    });
  }, [count]);

  // Compute lines in pixel space (uses size)
  const lines = useMemo(() => {
    const px = dots.map((d) => ({ x: (d.x / 100) * size.w, y: (d.y / 100) * size.h }));
    const out: Array<{ x1: number; y1: number; x2: number; y2: number; o: number }> = [];
    const threshold = Math.min(size.w, size.h) * 0.18;
    for (let i = 0; i < px.length; i++) {
      for (let j = i + 1; j < px.length; j++) {
        const dx = px[i].x - px[j].x;
        const dy = px[i].y - px[j].y;
        const d = Math.hypot(dx, dy);
        if (d < threshold) {
          out.push({
            x1: px[i].x,
            y1: px[i].y,
            x2: px[j].x,
            y2: px[j].y,
            o: 0.18 * (1 - d / threshold),
          });
        }
      }
    }
    return out;
  }, [dots, size]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className ?? ""}`}
      style={{
        transition: "background-color 0.6s ease",
        backgroundColor: tint ? "hsl(187 82% 55% / 0.04)" : "transparent",
      }}
    >
      <svg
        width={size.w}
        height={size.h}
        className="absolute inset-0"
        style={{ pointerEvents: "none" }}
      >
        {lines.map((l, i) => (
          <line
            key={i}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke="hsl(var(--primary-glow))"
            strokeOpacity={l.o}
            strokeWidth="0.6"
          />
        ))}
      </svg>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-primary-glow/70"
          style={{
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.r * 2,
            height: d.r * 2,
            // Use a wrapper transform via CSS keyframes (orbit defined inline)
            animation: `orbit-${i % 4} ${d.dur}s ease-in-out ${d.delay}s infinite`,
            // fallback: gentle breathe
          }}
        />
      ))}
      {/* inline keyframes for 4 orbit variants */}
      <style>{`
        @keyframes orbit-0 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(6px,-4px); } }
        @keyframes orbit-1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-5px,5px); } }
        @keyframes orbit-2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(4px,6px); } }
        @keyframes orbit-3 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-7px,-3px); } }
        @media (prefers-reduced-motion: reduce) {
          @keyframes orbit-0 { 0%,100% { transform: translate(0,0); } }
          @keyframes orbit-1 { 0%,100% { transform: translate(0,0); } }
          @keyframes orbit-2 { 0%,100% { transform: translate(0,0); } }
          @keyframes orbit-3 { 0%,100% { transform: translate(0,0); } }
        }
      `}</style>
    </div>
  );
}
